import React, { useState, useEffect, useRef } from 'react'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import { motion, AnimatePresence } from 'framer-motion'

export default function FloatingButtons() {
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [message, setMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isChatOpen])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const handleSend = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const phoneNumber = '27810634772'
    const textToSend = message.trim() || 'Hello! I am interested in your custom logo mats.'
    const encoded = encodeURIComponent(textToSend)
    window.open(`https://wa.me/${phoneNumber}?text=${encoded}`, '_blank')
    setMessage('')
  }

  return (
    <>
      {/* WhatsApp Chat Popover Widget */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-[110px] right-4 sm:right-6 z-50 w-[330px] sm:w-[360px] rounded-2xl shadow-2xl overflow-hidden border border-gray-200/80 bg-[#efeae2] flex flex-col font-sans"
          >
            {/* Dark Green WhatsApp Header */}
            <div className="bg-[#075e54] text-white px-4 py-3.5 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                    <WhatsAppIcon className="text-white" fontSize="medium" />
                  </div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-[#075e54] rounded-full" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-white leading-tight">WhatsApp</h3>
                  <p className="text-[11px] text-white/80 font-normal">
                    Logo Branded Door Mats • <span className="text-green-300 font-medium">Online</span>
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsChatOpen(false)}
                className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Close chat"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {/* Chat Body Area (WhatsApp Pattern Style) */}
            <div
              className="p-4 flex-1 min-h-[220px] max-h-[300px] overflow-y-auto space-y-3"
              style={{
                backgroundImage: `radial-gradient(#075e54 0.4px, transparent 0.4px), radial-gradient(#075e54 0.4px, #efeae2 0.4px)`,
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 8px 8px',
                opacity: 0.97,
              }}
            >
              {/* Agent Greeting Bubble */}
              <div className="flex flex-col items-start max-w-[88%]">
                <div className="bg-white text-gray-800 text-xs sm:text-sm p-3.5 rounded-2xl rounded-tl-none shadow-sm border border-gray-200/60 leading-relaxed">
                  <p className="font-semibold text-[#075e54] text-[11px] mb-1">Support Team</p>
                  👋 Hi! Welcome to Logo Branded Door Mats Johannesburg.
                  <br /><br />
                  How can we assist you with your custom entrance mat today?
                  <div className="text-[10px] text-gray-400 text-right mt-1.5 font-medium">Just now</div>
                </div>
              </div>
            </div>

            {/* Input & Send Form Bar */}
            <form onSubmit={handleSend} className="bg-[#f0f2f5] p-2.5 flex items-center gap-2 border-t border-gray-200">
              <input
                ref={inputRef}
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 bg-white text-gray-800 text-xs sm:text-sm px-4 py-2.5 rounded-full border border-gray-300/80 outline-none focus:ring-2 focus:ring-[#075e54] placeholder:text-gray-400 shadow-inner"
              />
              <button
                type="submit"
                className="w-10 h-10 rounded-full bg-[#075e54] hover:bg-[#054c44] text-white flex items-center justify-center flex-shrink-0 transition-all shadow-md active:scale-95"
                aria-label="Send WhatsApp message"
              >
                <SendIcon fontSize="small" className="-mr-0.5" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Buttons Container */}
      <div className="fixed bottom-[44px] sm:bottom-10 right-4 sm:right-6 z-40 flex flex-col gap-3">
        {/* WhatsApp Toggle Button */}
        <motion.button
          onClick={() => setIsChatOpen((prev) => !prev)}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-green-500 text-white shadow-xl hover:bg-green-600 transition-colors relative"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          aria-label="Toggle WhatsApp chat"
        >
          {isChatOpen ? <CloseIcon fontSize="medium" /> : <WhatsAppIcon fontSize="medium" />}
          {!isChatOpen && (
            <span className="absolute top-0 right-0 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-400 border-2 border-white"></span>
            </span>
          )}
        </motion.button>

        {/* Back to Top Button */}
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              onClick={scrollToTop}
              className="flex items-center justify-center w-14 h-14 rounded-full bg-pink-500 text-white shadow-xl hover:bg-pink-600 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              aria-label="Back to top"
            >
              <KeyboardArrowUpIcon fontSize="medium" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
